/**
 * @Copyright 2014-2017 DataVoke, Inc. All rights reserved.
 * Tools object to place utility type items that are used in numerous places
 */


'use strict';

import $ from 'jquery';
import {
    EntityTypes,
    PropertyDataType
} from './dvtypes';
import dateLib from './dateLib';
import {EntityLinker} from "../entities/entityLinker";
import {Dictionary} from './Dictionary';

export {Dictionary} from './Dictionary';
export * from './textFormatting';
export const dictionary = Dictionary;

/**
 * This is a guaranteed-unique value to stop the EventEmitter
 * from swallowing return values from event handlers.
 * @type {symbol}
 */
export const EMITTER_STOP_VALUE = Symbol(new Date().getTime().toString());

/**
 * A no-operation function
 */
export function noop() {}

let allPropIndicies = /\[(\w+)\]/g;
/**
 * Deep property getter
 * ex: `get(thing, 'a.b[0].c')`
 * @param {Object} o
 * @param {String} s
 * @returns {*}
 * @deprecated
 */
export function get(o, s) {
    if (o == null) {
        return null;
    }
    // Convert indexes to properties
    s = s.replace(allPropIndicies, '.$1');

    // Strip a leading dot
    if (s[0] === ".") {
        s = s.slice(1);
    }
    var a = s.split('.');
    for (var i = 0, n = a.length; i < n; ++i) {
        if (o == null) {
            return;
        }
        var k = a[i];
        if (k in o) {
            o = o[k];
        } else {
            return;
        }
    }
    return o;
}

/**
 * Return the value of the specified object's specified property chain; if not found, return default value.
 * ex: `get(thing, 'a.b[0].c', 1)`
 * @param {Object} o - Object to look for property chain of.
 * @param {string} s - String of the property chain.
 * @param {*} [def = null] - Default value
 * @return {*}
 * @deprecated
 */
export function getOrDefault(o, s, def = null) {
    let result = get(o, s);
    return (result != null ? result : def);
}

export async function waitAnimationFrame () {
    // need to return a Promise because we have a non-Promisable async call we have to wait for.
    return new Promise((resolve, reject) => {
        requestAnimationFrame(() => {
            resolve();
        });
    });
}

/**
 * Causes your desired class to inherit from a source class. This uses
 * prototypical inheritance so you can override methods without ruining
 * the parent class.
 *
 * This will alter the actual destination class though, it does not
 * create a new class.
 *
 * @param {Function} destination The target class for the inheritance.
 * @param {Function} source Class to inherit from.
 * @param {Boolean} addSuper Should we add the _super property to the prototype? Defaults to true.
 */
export function inherit(destination, source, addSuper) {
    var proto = destination.prototype = Object.create(source.prototype);
    proto.constructor = destination;

    if (addSuper || typeof addSuper === 'undefined') {
        destination._super = source.prototype;
    }
}

// Performs a deep clone operation...AL
export function deepClone(object, excludeProps) {
    if (object == null) {
        return null;
    }
    if (excludeProps == null) { excludeProps = []; }
    if (typeof excludeProps === "string") { excludeProps = [excludeProps]; }
    let excludedKeys = [],
        excludedObjs = [];
    excludeProps.forEach((excludedKey) => {
        excludedKeys.push(excludedKey);
        excludedObjs.push(object[excludedKey]);
        object[excludedKey] = undefined;
    });

    let retObject = deepExtend(object);

    function deepExtend(obj) {
        if (obj === null || typeof obj !== "object") {
            return obj;
        }

        let props = Object.getOwnPropertyDescriptors(obj),
            arrayNames = [],
            arrayValues = [];
        for (let prop in props) {
            if (Array.isArray(props[prop].value)) {
                // we do not create copies of the items in the array, just the array with the same references/values.
                arrayNames.push(prop);
                let newArray = [];
                props[prop].value.forEach((item) => {
                    newArray.push(deepExtend(item));
                });
                arrayValues.push(newArray);
                props[prop].value = null;
            }
            else if (Object.prototype.toString.call(props[prop].value) === '[object Date]') {
                props[prop].value = new Date(props[prop].value);
            }
            else {
                props[prop].value = deepExtend(props[prop].value);
            }
        }
        let newObj;
        if (Array.isArray(obj)) {
            newObj = [];
            for (let arrayProp in props) {
                if (props.hasOwnProperty(arrayProp) && arrayProp !== 'length') {
                    newObj.push(props[arrayProp].value);
                }
            }
        }
        else if (Object.prototype.toString.call(obj) === '[object Date]') {
            newObj = new Date(obj);
        }
        else {
            newObj = Object.create( Object.getPrototypeOf(obj), props );
        }
        for (let i = 0, il = arrayNames.length; i < il;  i++) {
            newObj[arrayNames[i]] = arrayValues[i];
        }
        return newObj
    }

    for (let i = 0, il = excludedKeys.length; i < il; i++) {
        object[excludedKeys[i]] = excludedObjs[i];
    }
    return retObject;
}

/**
 * Update toObj object reference with the properties from defaults object. Any properties in toObj
 * that have the same name as properties in defaults will be replaced with the values from defaults.
 * NOTE: This only applies top level properties on the specified defaults object.
 * @param {Object} defaults - Object reference with properties that will add or replace same properties in toObj
 * @param {Object} toObj - Object referecne to receive the properties from defaults object.
 * @return {*} - Return toObj reference with updated properties.
 */
export function applyDefaults (defaults, toObj) {
    if (defaults != null) {
        for (var defaultProp in defaults) {
            toObj[defaultProp] = defaults[defaultProp];
        }
    }

    return toObj;
}

/**
 * Merge properties of one object into another so that we "replace" them.
 * @param {object} sourceCopy - The object to overwrite properties from the merge object.
 * @param {object} mergingCopy - The object to merge into the source object.
 * @returns {*}
 */
export function mergeEntities(sourceCopy, mergingCopy) {
    for (let p in mergingCopy) {
        // Property in destination object set; update its value.
        if (mergingCopy[p] != null && typeof mergingCopy[p] === 'object' && !(mergingCopy[p] instanceof Array || mergingCopy[p] instanceof Date)) {
            if (sourceCopy[p] == null) {
                sourceCopy[p] = {};
                if (mergingCopy[p].__type != null) {
                    sourceCopy[p].__type = mergingCopy[p].__type;
                }
            }
            sourceCopy[p] = mergeEntities(sourceCopy[p], mergingCopy[p]);
        }
        else {
            sourceCopy[p] = mergingCopy[p];
        }
    }
    return sourceCopy;
}

//Alias to merge objects
export const mergeObjects = mergeEntities;

// provide access to the date.js "isNative()" function, which is pretty useful.
export const isNative = dateLib.isNative;

// User's language. Defaults to english.
export const language = (navigator.languages && navigator.languages[0]) || navigator.userLanguage || navigator.language || 'en-US';

let allxOry = /[xy]/g;
// This function will create / return a new UID.  (Used when generating a new Entity to send to the server...AL
export const newUID = ((typeof window.crypto !== 'undefined' && typeof window.crypto.getRandomValues === 'function')
|| (typeof window.msCrypto !== 'undefined' && typeof window.msCrypto.getRandomValues === 'function')) ?
    function() {
        // If we have a cryptographically secure PRNG, use that
        // http://stackoverflow.com/questions/6906916/collisions-when-generating-uuids-in-javascript
        var buf = new Uint16Array(8);
        if (typeof (window.crypto) != 'undefined')
            window.crypto.getRandomValues(buf);
        else
            window.msCrypto.getRandomValues(buf);

        var S4 = function(num) {
            var ret = num.toString(16);
            while (ret.length < 4) {
                ret = "0" + ret;
            }
            return ret;
        };
        return (S4(buf[0]) + S4(buf[1]) + "-" + S4(buf[2]) + "-" + S4(buf[3]) + "-" + S4(buf[4]) + "-" + S4(buf[5]) + S4(buf[6]) + S4(buf[7]));
    }

    :

    function() {
        // Otherwise, just use Math.random
        // http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/2117523#2117523
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(allxOry, function(c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

let nextTempID = -1;
// This function generates negative temporary IDs that are created on the client.  These IDs get updated after a save is performed to the server...AL
export function tempID() {
    return nextTempID--;
}

/**
 * Simple ascending collator; return the sorting comparison value for the specified values
 * @param {*} a - First value to compare
 * @param {*} b - Second value to compare
 * @returns {number} - -1 is to place a before b; 1 is to place b before a; 0 is to leave in current order
 */
function collatorEmulatorCompare (a, b) {
    if (isString(a)) {
        return a.localeCompare(b);
    }
    else {
        return a - b;
    }
}

class CollatorEmulator {
    /**
     * Simple ascending collator; return the sorting comparison value for the specified values
     * @param {*} a - First value to compare
     * @param {*} b - Second value to compare
     * @returns {number} - -1 is to place a before b; 1 is to place b before a; 0 is to leave in current order
     */
    compare (a, b) {
        return collatorEmulatorCompare(a, b);
    }
}

/**
 * Create a universal collator for the application.
 * @type {Intl.Collator|CollatorEmulator|null}
 */
export var collator = null;
if (typeof Intl === 'function') {
    // Intl is supported on this browser; use it
    collator = new Intl.Collator(language.split('-')[0], { sensitivity: "base" });
}
else {
    // Intl is not supported on this browser; fake it
    collator = new CollatorEmulator();
}

export function sortComplex(objectArray, sortAttribute, asc) {
    //NOTE: A copy must be made so the source array ref is not changed!!!  ... EK
    if (objectArray == null) return objectArray;
    var sortedArray = [];
    for (var i = 0, ilimit = objectArray.length; i < ilimit; i++) {
        sortedArray.push(objectArray[i]);
    }

    return sortedArray.sort(function(a, b) {
        function getAttrVal(obj) {
            var keys = sortAttribute.split(".");
            var objAttr = obj;

            for (var i = 0; i < keys.length; i++) {
                objAttr = objAttr[keys[i]];
                if (objAttr == null) break;
            }

            return objAttr;
        }

        if (asc)
            return collator.compare(getAttrVal(a), getAttrVal(b));
        else
            return collator.compare(getAttrVal(b), getAttrVal(a));
    });
}

export function groupBy(array, selectorFunction, groupItemSortKey) {
    if (array.length == 0) return [{ group: "", values: array }];

    var groupKeys = [];
    var returnArray = [];
    for (var i = 0, ilimit = array.length; i < ilimit; i++) {
        var thisItem = array[i];
        var groupKey = selectorFunction(thisItem);

        var keyPos = groupKeys.indexOf(groupKey);
        if (keyPos == -1) {
            groupKeys.push(groupKey);
            returnArray.push({ group: groupKey, values: [thisItem] });
        }
        else {
            returnArray[keyPos].values.push(thisItem);
        }
    }

    //Sort the group keys (asc for now) ... EK
    returnArray = sortComplex(returnArray, "group", true);

    //sort the array by group key based on the item sortk key specified...EK
    for (var i = 0, ilimit = returnArray.length; i < ilimit; i++) {
        returnArray[i].values = sortComplex(returnArray[i].values, groupItemSortKey, true);
    }

    return returnArray;
}

/**
 * The code used for the sortMerge() method of array prototypes is sourced from StackExchange
 *
 * http://codereview.stackexchange.com/questions/87000/fast-merge-sort-in-javascript
 *
 * and as such the code for this method is licensed under Creative Commons
 * Attribution-ShareAlike license version 3.0 Unported (CC BY-SA 3.0):
 * http://stackexchange.com/legal
 *
 * You are free to:
 * Share — copy and redistribute the material in any medium or format
 * Adapt — remix, transform, and build upon the material
 * for any purpose, even commercially.
 *
 * The licensor cannot revoke these freedoms as long as you follow the license terms.
 *
 * Under the following terms:
 *  * Attribution — You must give appropriate credit, provide a link to the license, and indicate
 *    if changes were made. You may do so in any reasonable manner, but not in any way that
 *    suggests the licensor endorses you or your use.
 *  * ShareAlike — If you remix, transform, or build upon the material, you must distribute your
 *    contributions under the same license as the original.
 *  * No additional restrictions — You may not apply legal terms or technological measures that
 *    legally restrict others from doing anything the license permits.
 *
 * Notices:
 * You do not have to comply with the license for elements of the material in the public domain
 * or where your use is permitted by an applicable exception or limitation.
 * No warranties are given. The license may not give you all of the permissions necessary for your
 * intended use. For example, other rights such as publicity, privacy, or moral rights may limit
 * how you use the material.
 *
 * NOTICE OF USE: This code was *not* adapted from its presented form; since the ShareAlike
 * provision has not been triggered (as the material has not been remixed, transformed or built
 * upon, but merely used), other code does not have to be licensed under CC BY-SA 3.0.
 *
 * @param {Array} array - The array to sort
 * @param {Function|null} [cmp = null] - Comparison function to use for the sort.
 */
export function mergeSort (array, cmp = null) {
    /*
     Merge sort.
     On average, two orders of magnitude faster than Array.prototype.sort() for
     large arrays, with potentially many equal elements.
     Note that the default comparison function does not coerce its arguments to strings.
     */

    if (cmp == null) {
        // Note: This is not the same as the default behavior for Array.prototype.sort(),
        // which coerces elements to strings before comparing them.
        cmp = function (a, b) {
            return a < b ? -1 : a === b ? 0 : 1;
        };
    }

    /**
     * Perform a merge on the parent function array based on the locations specified
     * @param {number} begin - Index to begin the merge
     * @param {number} begin_right - Index to the edge of the merge
     * @param {number} end - Index to the end of the merge
     */
    function merge (begin, begin_right, end) {
        // Create a copy of the left and right halves.
        let left_size = begin_right - begin,
            right_size = end - begin_right,
            left = array.slice(begin, begin_right),
            right = array.slice(begin_right, end);

        // Merge left and right halves back into original array.
        let i = begin,
            j = 0,
            k = 0;

        while (j < left_size && k < right_size) {
            if (cmp(left[j], right[k]) <= 0) {
                array[i++] = left[j++];
            }
            else {
                array[i++] = right[k++];
            }
        }

        // At this point, at least one of the two halves is finished.
        // Copy any remaining elements from left array back to original array.
        while (j < left_size) {
            array[i++] = left[j++];
        }

        // Copy any remaining elements from right array back to original array.
        while (k < right_size) {
            array[i++] = right[k++];
        }
    }

    /**
     * Sort function for this merge/sort
     * @param {number} begin - Start of the merge/sort
     * @param {number} end - End of the merge/sort
     */
    function msort (begin, end) {
        let size = end - begin;

        if (size <= 8) {
            // By experimentation, the sort is fastest when using native sort for
            // arrays with a maximum size somewhere between 4 and 16.
            // This decreases the depth of the recursion for an array size where
            // O(n^2) sorting algorithms are acceptable.
            let sub_array = array.slice(begin, end);

            sub_array.sort(cmp);

            // Copy the sorted array back to the original array.
            for (let i = 0; i < size; ++i) {
                array[begin + i] = sub_array[i];
            }

            return;
        }

        let begin_right = begin + (size >> 1);

        // sort and merge the larger blocks
        msort(begin, begin_right);
        msort(begin_right, end);
        merge(begin, begin_right, end);
    }

    msort(0, array.length);

    return array;
}

/**
 * Does toLocaleString support giving it a locale?
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/toLocaleString#Checking_for_support_for_locales_and_options_arguments
 * @type boolean
 */
var toLocaleStringSupportsLocales = (function() {
    var number = 0;
    try {
        number.toLocaleString('i');
    } catch (e) {
        return e.name === 'RangeError';
    }
    return false;
}());

/**
 * Get's the character that would be used to separate 1009 (',' in english)
 * @param {string|null} userLanguage - ex. 'de-DE'
 * @private
 */
function getSeparator(userLanguage) {
    var separator;
    try {
        separator = Number(1009).toLocaleString(userLanguage).substring(1, 2);
    } catch (e) {
        separator = ',';
    }
    return separator;
}

/**
 * Get's the character that would be used for a decimal ('.' in english)
 * @param {string|null} userLanguage - ex. 'de-DE'
 * @returns {string}
 * @private
 */
function getDecimal(userLanguage) {
    return Number(9.8).toLocaleString(userLanguage).substring(1, 2);
}

let MYSQL_INTEGER_MAX = 2147483647;
let MYSQL_INTEGER_MIN = -2147483648;
let NUMBER_SEPARATOR_CHARACTER = toLocaleStringSupportsLocales ? getSeparator(language) : ',';
export let NUMBER_DECIMAL_CHARACTER = toLocaleStringSupportsLocales ? getDecimal(language) : ".";

let allWhitespace = /\s+/g;
/**
 * @param {string|number} value
 * @param {object} [options]
 * @param {boolean} [options.fullObject]
 */
export function validateDecimal(value, options) {
    options = options || {};

    let validation = {
        value: null,
        response: false
    };

    let isNegative = false,
        isPercent = false;

    if (typeof value === 'string') {
        // remove whitespace
        value = value.replace(allWhitespace, "");

        if (value.indexOf("-") > -1) {
            value = value.replace("-", "");
            isNegative = true;
        }

        if (value[0] === "(" && value[value.length - 1] === ")") {
            value = value.substring(1, value.length - 1);

            // Parens around a number is 'negative' for accountants
            isNegative = true;
        }

        var decimalIndex = value.indexOf(NUMBER_DECIMAL_CHARACTER);
        var separatorIndex = value.indexOf(NUMBER_SEPARATOR_CHARACTER);

        //decimal was found before comma (or regional equivalent) which is not valid...TD
        if (decimalIndex > -1 && separatorIndex > -1 && decimalIndex < separatorIndex) {
            validation.value = value;
            return options.fullObject ? validation : validation.response;
        }

        //remove separator character and check for currency character (front or back)...TD
        value = value.replace(new RegExp(NUMBER_SEPARATOR_CHARACTER, 'g'), '');
        var firstDigit = value[0];
        var lastDigit = value[value.length - 1];

        if (isNaN(Number(firstDigit)) && firstDigit !== "-" && firstDigit != NUMBER_DECIMAL_CHARACTER) {
            value = value.substring(1, value.length);
        }
        if (isNaN(Number(lastDigit))) {
            if (lastDigit === "%") {
                isPercent = true;
            }
            value = value.substring(0, value.length - 1);
        }

        value = Number(value);

        // add back in our negative symbol if necessary
        if (isNegative) {
            value = -value;
        }

        // Move the decimal point back to non-percentage
        if (isPercent) {
            value = value / 100;
        }

        if (Number.isNumeric(value) && value <= MYSQL_INTEGER_MAX && value >= MYSQL_INTEGER_MIN) {
            validation.response = true;
        }
    }
    else {
        validation.response = typeof value === 'number';
    }

    validation.value = value;

    return options.fullObject ? validation : validation.response;
}

/**
 *
 * @param {string|number} value
 * @param {object} [options]
 * @param {boolean} [options.returnFullObject] - return full validation object, or just the boolean
 * @returns {*}
 */
export function validateInteger(value, options) {
    options = options || {};

    let validation = {
        value: null,
        response: false
    };

    let isNegative = false,
        isPercent = false;

    if (typeof value === 'string') {
        // remove whitespace
        value = value.replace(allWhitespace, '');

        if (value.indexOf("-") > -1) {
            value = value.replace("-", "");
            isNegative = true;
        }

        if (value[0] === "(" && value[value.length - 1] === ")") {
            value = value.substring(1, value.length - 1);

            // Parens around a number is 'negative' for accountants
            isNegative = true;
        }

        let decimalIndex = value.indexOf(NUMBER_DECIMAL_CHARACTER);
        let separatorIndex = value.indexOf(NUMBER_SEPARATOR_CHARACTER);

        //decimal was found before comma (or regional equivalent) which is not valid...TD
        if (decimalIndex > -1 && separatorIndex > -1 && decimalIndex < separatorIndex) {
            validation.value = value;
            return options.fullObject ? validation : validation.response;
        }

        //remove separator character and check for currency character (front or back)...TD
        value = value.replace(new RegExp(NUMBER_SEPARATOR_CHARACTER, 'g'), '');
        var firstDigit = value[0];
        var lastDigit = value[value.length - 1];

        if (isNaN(Number(firstDigit)) && firstDigit !== "-") {
            value = value.substring(1, value.length);
        }
        if (isNaN(Number(lastDigit))) {
            if (lastDigit === "%") {
                isPercent = true;
            }
            value = value.substring(0, value.length - 1);
        }

        value = parseInt(value, 10);

        // add back in our negative symbol if necessary before we turn this string back into an int...TD
        if (isNegative) {
            value = -value;
        }

        // Move the value back to non-percentage
        if (isPercent) {
            value = Math.round(value / 100);
        }

        if (Number.isNumeric(value) && value <= MYSQL_INTEGER_MAX && value >= MYSQL_INTEGER_MIN) {
            validation.response = true;
        }
    }
    else if (typeof value === 'number') {
        value = parseInt(value, 10);
        validation.response = true;
    }
    else {
        validation.response = false;
    }

    validation.value = value;

    return options.fullObject ? validation : validation.response;
}

var validBits = [0, 1, true, false, '0', '1', 'true', 'false', 'yes', 'y', 'no', 'n'];

/**
 * This function will validate a value based off of the enum type passed in (enum types defined in PropertyDataType)...AL
 * @param type
 * @param {string|number|Date|boolean} value
 * @returns {{value: null, response: boolean}}
 */
export function validateValue(type, value) {
    var validation = {
        value: null,
        response: false
    };

    // null is always a valid value for any data type.
    if (value == null || value === "") {
        value = null;
        validation.value = value;
        validation.response = true;
        return validation;
    }

    switch (type) {
        case PropertyDataType.Bit:
            validation.value = value;

            // no need to check null or empty string, since checked above
            if (validBits.indexOf(typeof value === 'string' ? value.toLowerCase() : value) > -1) {
                validation.response = true;
            }

            return validation;
            break;
        case PropertyDataType.Integer:
            return validateInteger(value, { fullObject: true });
            break;
        case PropertyDataType.Decimal:
            return validateDecimal(value, { fullObject: true });
            break;
        case PropertyDataType.DateTime:
        case PropertyDataType.StaticDateTime:
            validation.value = value;
            validation.response = true;
            try {
                var test = new Date(value), subtest;
                if (test.toString() == "Invalid Date") {
                    //If the original try is invalid try again by parsing the value first.BC
                    //if it is an time removed the PM or date.Parse returns null see Bug DF-2404...MD

                    if (value.toString().toLowerCase().indexOf("pm") > -1 || value.toLowerCase().indexOf("am") > -1) {
                        //do not allow invalid times
                        if (value.toString().toLowerCase().indexOf(" pm") > -1 || value.toString().toLowerCase().indexOf(" am") > -1) {
                            // this means  am and pm are in the correct format --> validation = true
                        } else {
                            validation.response = false;
                        }
                        return validation;
                    }
                    //If the original try is invalid try again by parsing the value first...BC
                    subtest = Date.parse(value);
                    if (subtest != null) {
                        test = new Date(subtest);
                    }
                    else {
                        //If the parsed value is null, it is an invalid date
                        validation.response = false;
                    }
                }

            }
            catch (e) {
                validation.response = false;
            }
            return validation;
            break;
        case PropertyDataType.List:
        case PropertyDataType.Text:
        case PropertyDataType.Binary:
        case PropertyDataType.RTF:
        case PropertyDataType.InlineRTF:
            validation.value = value;
            validation.response = true;
            return validation;
            break;
        default:
            // Unknown type specified
            if(process.env.NODE_ENV === 'development' || process.env.DEBUG) {
                console.warn(`validateValue detected an unknown data type (${String(type).toString()}) while attempting to validate value "${value}"`);
            }
            return validation;
    }
}

//This function takes a DataType parameter and returns the name of the DataType as a string...TD
export function getDataTypeName(dataType) {
    var result;
    if (dataType == null) {
        dataType = -1;
    }
    switch (dataType) {
        case PropertyDataType.Bit:
            result = "Bit";
            break;
        case PropertyDataType.Decimal:
            result = "Decimal";
            break;
        case PropertyDataType.DateTime:
            result = "DateTime";
            break;
        case PropertyDataType.Integer:
            result = "Integer";
            break;
        case PropertyDataType.Text:
            result = "Text";
            break;
        case PropertyDataType.Binary:
            result = "Binary";
            break;
        case PropertyDataType.RTF:
            result = "RTF";
            break;
        case PropertyDataType.InlineRTF:
            result = "InlineRTF";
            break;
        case PropertyDataType.StaticDateTime:
            result = "StaticDateTime";
            break;
        case PropertyDataType.PrimaryKey:
            result = "PrimaryKey";
            break;
        default:
            result = "Unknown DataType";
    }

    return result;
}

export function sum(arr) {
    var total = 0;
    for (var i in arr) {
        if (!isNaN(arr[i])) { total += arr[i]; }
    }
    return total;
}

export function getHtmlPosition(element) {
    var x = 0, y = 0;
    while (element) {
        x += (element.offsetLeft - element.scrollLeft + element.clientLeft);
        y += (element.offsetTop - element.scrollTop + element.clientTop);
        element = element.offsetParent;
    }
    return { x: x, y: y };
}

/**
 * Another object would like to subscribe to the event specified
 * @param {Object} subs - Subscription object of the object that will be sending notifications
 * @param {string} event - The name of hte event to unsubscribe to
 * @param {*} src - The requester of the event notification. This is used to uniquely ID the subscription.
 * @param {Function} callback - The function to call back when the event is triggered
 * @param {EventEmitter|null} [ee = null] - The event emitter supporting object that will emit the event
 */
export function subscribe(subs, event, src, callback, ee = null) {
    if (!subs.hasOwnProperty(event)) {
        subs[event] = [];
    }

    let subArray = subs[event],
        found = subArray.find(a => a[0] === src);

    if (found) {
        found[1] = callback;
    }
    else {
        subArray.push([src, callback]);
    }

    if (ee) {
        // go ahead and setup the EE listener as well.
        ee.on(event, callback);
    }
}

/**
 * Another object would like to unsubscribe from the event specified.
 * @param {Object} subs - Subscription object of the object that will be sending notifications
 * @param {string} event - The name of hte event to unsubscribe to
 * @param {*} src - The requester of the event notification. This is used to uniquely ID the subscription.
 * @param {EventEmitter|null} [ee = null] - The event emitter supporting object that will emit the event
 */
export function unSubscribe(subs, event, src, ee = null) {
    if (!subs.hasOwnProperty(event)) {
        return;
    }

    let subArray = subs[event];

    for (let i = subArray.length - 1; i >= 0; i--) {
        let sub = subArray[i];
        if (sub[0] === src) {
            if (ee) {
                // go ahead and ensure that we release the EE listener as well.
                ee.off(event, sub[1]);
            }
            sub.length = 0;
            subArray.splice(i, 1);
            break;
        }
    }
}

/**
 * This will clear all subscriptions from the passed subscription object.
 * @param {Object} subs - Subscription object of the object that will be sending notifications
 * @param {string} event - The name of hte event to unsubscribe to
 * @param {EventEmitter|null} [ee = null] - The event emitter supporting object that will emit the event
 */
export function clearSubscriptions(subs, event, ee = null) {
    let list = subs[event];
    if (list == null) {
        return;
    }

    // these should be arrays of arrays; clear them out!
    list.forEach((sub) => {
        if (Array.isArray(sub)) {
            if (ee) {
                // go ahead and ensure that we release the EE listener as well.
                ee.off(event, sub[1]);
            }
            sub.length = 0;
        }
    });

    list.length = 0;
}

/**
 * Retrieve an image from the image library for use.
 * @param {string} sourceName - The name of the image library resource to retrieve.
 * @param {number} [width = 25] - The specified width of the image (DEFAULT is 25).
 * @param {number} [height = 25] - The specified height of the image (DEFAULT is 25).
 * @returns {*}
 */
export function getImageByName(sourceName, width = 25, height = 25) {
    if (width == null) width = 25;
    if (height == null) height = 25;

    let img = app.dv.imageMgr.getImage(iconPathToName(sourceName));  //Get the image svg reference from the library...EK
    img.width = width;
    img.height = height;

    let pic = new zebra.ui.dvPicture(img);      //Get a picture (view) to render the image ... EK
    pic.gap = 0;
    pic.width = width;
    pic.height = height;

    return pic;
}

/**
 * This returns an dvImagePan object with an SVG referenced from the global imageMgr...EK
 * @param {String} sourceName - the name of the SVG icon to get from the image manager
 * @param {Number} [width = 25] - the width of the dvImagePan
 * @param {Number} [height = 25] - the height of the dvImagePan
 * @returns {zebra.ui.dvImagePan} An imagePan with the specified SVG icon
 */
export function getImagePanByName(sourceName, width = 25, height = 25) {
    if (width == null) width = 25;
    if (height == null) height = 25;

    let pic = getImageByName(sourceName, width, height),
        imgpan = new zebra.ui.dvImagePan(pic);  //The panel is how the image is layed out ...EK

    imgpan.view.color = zebra.ui.palette.Primary2;
    imgpan.setPreferredSize(width, height);
    imgpan.setPadding(0);
    return imgpan;
}

/**
 * Gets a dvImagePan with the image URL specified, or null if the URL is invalid
 * NOTE: if the url specified is an SVG XML string, it will load that and return the image panel.
 * @param {String} url - The URL to get the image from
 * @param {Number} [width = 25] - the width of the dvImagePan
 * @param {Number} [height = 25] - the height of the dvImagePan
 * @returns {Promise} A promise that resolves with an imagePan with the specified URL image, or null if the URL is invalid
 */
export async function getImagePanByURL(url, width = 25, height = 25) {
    let picture = new zebra.ui.dvPicture();
    picture.gap = 0;
    picture.width = width;
    picture.height = height;

    if (url) {
        let img = new Image();

        return new Promise((resolve) => {
            img.onload = () => {
                // The image data is done loading, set the data to the imgPan and return
                picture.setTarget(img);
                let imgPan = new zebra.ui.dvImagePan(picture);
                imgPan.view.color = zebra.ui.palette.Primary2;
                imgPan.setPreferredSize(width, height);
                imgPan.setPadding(0);
                resolve(imgPan);
            };
            img.onerror = () => {
                // The image data failed, return null
                resolve(null)
            };

            let valCheck = url.toLowerCase();

            if (valCheck.substring(0,4) !== 'data:'
                && valCheck.contains('<svg')
                && valCheck.contains('</svg>')) {
                img.svgText = url;

                // the SVG is internally interpreted; it is not loaded, so the onload will not fire.
                picture.setTarget(img);
                let imgPan = new zebra.ui.dvImagePan(picture);
                imgPan.view.color = zebra.ui.palette.Primary2;
                imgPan.setPreferredSize(width, height);
                imgPan.setPadding(0);
                resolve(imgPan);
            }
            else {
                //Use the url as the source of the image.
                img.src = url;
            }
        })
    }
}

export function svgToImage (svgXml, width, height) {
    var img = new Image();
    if (height != null) { img.height = height; }
    if (width != null) { img.width = width; }
    img.src = "";
    img.svgText = svgXml;
    return img;
}

export function base64ToImage (base64Data, width, height) {
    var img = new Image();
    if (height != null) { img.height = height; }
    if (width != null) { img.width = width; }
    img.src = "data:image/gif;base64," + base64Data;
    return img;
}

export function arrayBufferToBase64(buffer){
    var binary = '',
        bytes = new Uint8Array(buffer),
        len = bytes.byteLength;

    // check if we are getting in a JSON'ed buffer...
    if (bytes.length === 0 && buffer.data != null && Array.isArray(buffer.data)) {
        bytes = new Uint8Array(buffer.data);
        len = bytes.byteLength;
    }

    for (var i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[ i ]);
    }
    return window.btoa(binary);
}

export function result (success, objError) {
    var r = {};

    r.success = success;
    r.err = objError;

    return r;
}

/**
 * Check if specified object or value is a "string" instance.
 * @param {*} o - Object or value to check if it is a "string" instance
 * @returns {boolean}
 */
export function isString (o) {
    return typeof o !== "undefined" && o !== null &&
        (typeof o === "string" || o.constructor === String);
}

/**
 * Check if the specified object or value is a "number" instance.
 * @param {*} o - Object or value to check if it is a "number" instance
 * @returns {boolean}
 */
export function isNumber(o) {
    return typeof o !== "undefined" && o !== null &&
        (typeof o === "number" || o.constructor === Number);
}

/**
 * Check if the specified object or value is a "boolean" instance
 * @param {*} o - Object or value to check if it is a "boolean" instance
 * @returns {boolean}
 */
export function isBoolean(o) {
    return typeof o !== "undefined" && o !== null &&
        (typeof o === "boolean" || o.constructor === Boolean);
}

/**
 * Attempt to get the -general- Property Data Type for the specified value
 * NOTE: Will be unable to return StaticDateTime (will only return DateTime for date values)
 * @param {*} value
 * @returns {PropertyDataType|number}
 */
export function getDataTypeForValue (value) {
    if (isBoolean(value)) {
        return PropertyDataType.Bit;
    }
    else if (isNumber(value)) {
        return PropertyDataType.Integer;
    }
    else if (isDate(value)) {
        // This has passed both tests for date object; it must be a date
        return PropertyDataType.DateTime;
    }
    else {
        return PropertyDataType.Text;
    }
}

let functionRegex = /^function\s+([^\s(]+)/;
export function functionName (f) {
    var mt = f.toString().match(functionRegex);
    return (mt == null) ? f.name : mt[1];
}

export function xmlDoc (xmlString) {
    var tx = {}
    tx.xmlString = xmlString;
    tx.xmlDoc = null;


    tx.parse = function(xmlString) {
        tx.xmlString = xmlString;
        tx.xmlDoc = $.parseXML(xmlString);
    };

    tx.getKeys = function(keyName) {
        var $xml = $(tx.xmlDoc);
        return $xml.find(keyName);
    };

    /**
     * This will search an array of XML nodes for the specified key/value pair. When a
     * keyTagName XML element has a innerXML value of "searchText", then the innerXML of
     * the corresponding valueTagName XML element is returned.
     * If the searchText is not found as a value for any keyTagName XML element, null is
     * returned.
     * This assumes that the tags are in this arrangement:
     *
     *  <key>
     *      <keyTagName>searchText</keyTagName>
     *      <valueTagName>value</valueTagName>
     *  </key
     * @/// <param name="keys" type="Array">Array of XML nodes.</param>
     * @/// <param name="keyTagName" type="String">XML tag name for the key XML element in the node array.</param>
     * @/// <param name="valueTagName" type="String">XML tag name for the value XML element in the node array.</param>
     * @/// <param name="searchText" type="String">The text to search for in the key XML element values.</param>
     */
    tx.getValueForKey = function(keys, keyTagName, valueTagName, searchText) {
        for (var i = 0, limit = keys.length; i < limit; i++) {
            var key = $(keys[i]).find(keyTagName).text();
            if (key == searchText) {
                return $(keys[node]).find(valueTagName).text();
                break;
            }
        }
        return null;
    };

    if (xmlString != null) { tx.parse(xmlString); }
    return tx;
}

export function displayFileSize (bytes) {
    var exp = Math.log(bytes) / Math.log(1024) | 0;
    var result = (bytes / Math.pow(1024, exp)).toFixed(2);

    return result + ' ' + (exp == 0 ? 'bytes' : 'KMGTPEZY'[exp - 1] + 'B');
}

export function iconPathToName (iconPath) {
    // the icon path is expected to start "images/", so scrap that.
    if (iconPath.substring(0, 7) == "images/") {
        iconPath = iconPath.substring(7);
    }
    if (iconPath.substring(0, 41) == "DataVoke.ImageLibrary.Images.StatusIcons.") {
        iconPath = "statusicons/" + iconPath.substring(41);
    }
    // now remove the file extension.
    if (iconPath.indexOf(".") > -1) {
        iconPath = iconPath.substring(0, iconPath.lastIndexOf("."));
    }
    return iconPath;
}

/**
 * Formats a value using the given .NET format. Uses JQuery formatting, and stores references of  calculated formats to reduce computations.
 * NOTE: Currently formats only the value, additional text in the format  string should be handled separately as prefixes and suffixes.
 *
 * Currently supported formats:
 * Decimal
 * Currency
 * Percent
 */
export function parseMSSerializedDateString(dateString) {
    var t = dateString.replace("/Date(", "").replace(")/", ""),
        z = "", tzHours = 0, tzMinutes = 0, dateNeg = false, neg = false;

    // if, for any reason, we have a "-" or "+" at position 0, we need to remove it.
    switch (t.substring(0, 1)) {
        case "-":
            dateNeg = true;
        case "+":
            t = t.substring(1);
            break;
        default:
            break;
    }

    if (t.indexOf("-") > -1) {
        // this is West of GMT.
        z = t.substring(t.indexOf("-") + 1);
        t = t.substring(0, t.indexOf("-"));
        neg = true;
    } else if (t.indexOf("+") > -1) {
        // this is East of GMT.
        z = t.substring(t.indexOf("+") + 1);
        t = t.substring(0, t.indexOf("+"));
    } else {
        // no time zone information? OK...
        z = "0000";
    }

    tzHours = z.substring(0, 2);
    tzMinutes = z.substring(2, 4);
    var tz = parseFloat(tzHours + "." + tzMinutes);
    if (neg) { tz = 0 - tz; }
    return {
        date: new Date(parseFloat((dateNeg ? "-" : "") + t)),
        tzOffset: tz
    }
}

export const fontSizes = new Dictionary();

export function generateFontSizeTable(font) {
    var d = new Dictionary();

    for (var i = 32; i < 127; i++) {
        var c = String.fromCharCode(i);
        d.add(c, font.stringWidth(c));
    }

    return d;
}

let allNaN = /\b\":NaN\b/g;
/*
 * Parses entities from the server (deserialize / clean definition string)...AL
 */
export function entityParse(ent) {
    if (!ent) return;

    // Deserialize the entitiy (replace any NaN with 0)...AL
    ent = JSON.parse(ent.replace(allNaN, "\":0"));

    // Deserialize the definition (replace any NaN with 0)...AL
    if (ent.Definition) ent.Definition = JSON.parse(ent.Definition.replace(allNaN, "\":0"));

    ent.clientProperties = {};

    return ent;
}

/*
 * Returns a microsoft friendly date time per the DataContractJsonSerializer specs...AL
 */
export function toMSDateTime(dateTime, toDefString) {

    if (!dateTime) return null;

    if (toDefString == true && (typeof dateTime == "string" || dateTime instanceof String) && dateTime.indexOf("Date(") != -1) {
        // We need to inject an escape backslash in front of the exising backslashes.  When the serializer evaulates them, it strips them out.  Since the
        // JSON serializer (stringify) doesn't understand this date, it will not handle this for us...AL
        dateTime = "\\" + dateTime.substring(0, dateTime.length - 1) + "\\/"
        return dateTime;
    }

    var eT = dateTime.getTime(),
        offH = new Date().getTimezoneOffset() / 60,
        returnDate;

    if (offH.toString().length == 1) {
        if (toDefString == true)
            returnDate = "\\/Date(" + eT.toString() + "-0" + offH.toString() + "00)\\/";
        else
            returnDate = "\/Date(" + eT.toString() + "-0" + offH.toString() + "00)\/";
    }
    else if (offH.toString().length == 2) {
        if (toDefString == true)
            returnDate = "\\/Date(" + eT.toString() + "-" + offH.toString() + "00)\\/";
        else
            returnDate = "\/Date(" + eT.toString() + "-" + offH.toString() + "00)\/";
    }
    else
        returnDate = null;

    return returnDate;
}

export function adjustDateForDST(date) {
    var origTZ = date.getTimezoneOffset();
    date.addHours(origTZ / 60);
    var newTZ = date.getTimezoneOffset();
    if (origTZ != newTZ) {
        date.addHours((newTZ - origTZ) / 60);
    }
    date.addHours(0 - (date.getTimezoneOffset() / 60));
    return date;
}

/**
 * Determines if the date string will be parsed in ISO format
 * @param {String} dateString
 */
let isISORegex = /^\d{4}(-\d{2}){0,2}$/;
export function isDateStringISO(dateString) {
    return isISORegex.test(dateString);
}

export function getCacheObjects(linkerArray) {
    try {
        var result = [];
        for (var i = 0, j = linkerArray.length; i < j; i++) {
            var linker = linkerArray[i];
            switch (String(linker.type).toLowerCase) {
                case "category":
                    result.push(app.dv.cache.categories.getVal(linker.UID));
                    break;
                //case "Config":
                //    result.push(app.dv.cache.config.getVal(linker.UID));
                //    break;
                case "property":
                    result.push(app.dv.cache.properties.getVal(linker.UID));
                    break;
                case "recordtype":
                    result.push(app.dv.cache.recordTypes.getVal(linker.UID));
                    break;
                case "securitygroup":
                    result.push(app.dv.cache.securityGroups.getVal(linker.UID));
                    break;
                case "valuestatus":
                    result.push(app.dv.cache.valueStatuses.getVal(linker.UID));
                    break;
                case "view":
                    result.push(app.dv.cache.views.getVal(linker.UID));
                    break;
                default:
                    break;
            }
            if (result.length === 0) {
                return null;
            }
            else {
                return result;
            }
        }
    }
    catch (e) {
        //console.log(e.message);
        return null;
    }
}

export function copyToClipboard(s) {
    var ret = false;
    if (window.clipboardData && clipboardData.setData) {
        window.clipboardData.setData('text', s);
        ret = true;
    }
    else {
        // If we are here, we are in chrome (chromium).  We have to fake an element and execute the copy command on the elemnt to inject into the clipboard
        // This is a work around for chrome's security on copying to clipboard.  NOTE: at the time this was written the only other alternative was to use flash...AL
        var textArea = document.createElement('textarea');
        textArea.setAttribute('style', 'width:1;border:0;opacity:0;z-index:10000;');
        document.body.appendChild(textArea);
        textArea.value = s;
        textArea.select();
        textArea.focus();
        ret = document.execCommand('copy', false, null);
        document.body.removeChild(textArea);
    }
    return ret;
}

let allRegexSpecialCharacters = /[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g;
/**
 * Escapes regex special characters to allow for these characters to be used in regex search values
 * @param {String} str - The string that needs escaped
 * @return {String} - The string with escaped regex characters.
 */
export function escapeRegExp(str) {
    return str.replace(allRegexSpecialCharacters, "\\$&");
}

/**
 * Convert Hex color string to RGBA string
 * @param {String} hex
 * @param {Number} alpha
 * @returns {String}
 */
export function hexToRgbaString (hex, alpha) {
    let color = parseHexToRBG(hex);
    if (alpha == null) {
        alpha = '1.0';
    }
    return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
}

/**
 * Converts a hex color string to an object with r, g and b properties. (Defaults to black if hex is invalid)
 * @param {string} hex - the hex string to parse
 * @returns {Object} An object containing "r", "g", and "b" number properties
 */
const shorthandHexRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
const resultHexRegex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
export function parseHexToRBG(hex){
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    hex = hex.replace(shorthandHexRegex, function(m, r, g, b) {
        return r + r + g + g + b + b;
    });

    let result = resultHexRegex.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : {r:0, g:0, b:0};
}

/**
 * Converts an rgb object to a hex string
 * @param rgb
 * @returns {string}
 */
export function parseRGBToHex(rgb) {
    function componentToHex(c) {
        let hex = c.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    }

    return "#" + componentToHex(parseInt(rgb.r)) + componentToHex(parseInt(rgb.g)) + componentToHex(parseInt(rgb.b));
}

/**
 * Takes a string and parses it into an array of 4 numbers.
 * It accepts 1, 2, or 4 values seperated by commas.
 * For example: parsePaddingString('5, 10') -> [5, 10, 5, 10]
 * @param {String} s - The string to parse
 * @returns {Array|null} An array of four number (T, L, B, R) if the string was parsed correctly, or null
 */
let allBracketsAndWhitespace = /[\[\]\s]/g;
export function parsePaddingString(s) {
    try {
        let splitArr = s.replace(allBracketsAndWhitespace, '').split(",");
        if (splitArr.length === 1) {
            // All sides get the same value
            return [Number(splitArr[0]) || 0, Number(splitArr[0]) || 0, Number(splitArr[0]) || 0, Number(splitArr[0]) || 0];
        }
        else if (splitArr.length === 2) {
            // The first value is top and bottom, the second value is left and right
            return [Number(splitArr[0]) || 0, Number(splitArr[1]) || 0, Number(splitArr[0]) || 0, Number(splitArr[1]) || 0];
        }
        else if (splitArr.length === 3) {
            // Just default the missing value (right) to 0
            return [Number(splitArr[0]) || 0, Number(splitArr[1]) || 0, Number(splitArr[2]) || 0, 0];
        }
        else if (splitArr.length >= 4) {
            // The string already splits into each respective side
            return [Number(splitArr[0]) || 0, Number(splitArr[1]) || 0, Number(splitArr[2]) || 0, Number(splitArr[3]) || 0];
        }
        else {
            return null;
        }
    } catch (e) {
        return null;
    }
}

/**
 * Log the action to the console...eg
 * @param {Object} info is the object that is used to log information about the action.
 */
export function logActionToConsole(info) {
    try{
        //Ensure that information was passed in to be logged...eg
        if (info == null || info.chainID == 0) {
            //No info is passed in
            return false;
        }

        //Set the log information and write to the console...eg
        if (api._debugModeExpanded) {
            //Expand the group in the console...eg
            console.group((info.disabled ? "Skipped" : "Executed") + " Action: " + info.actionTypeName);
        } else{
            //Collapse the group in the console...eg
            console.groupCollapsed((info.disabled ? "Skipped" : "Executed") + " Action: " + info.actionTypeName);
        }
        console.log("View: " + info.viewName + "  (ID:" + info.viewID + ")");
        console.log("Task: " + info.chainName + " (ID:" + info.chainID + "  Step:" + info.step + ")");

        //Only Show record code and comment if there is a value there...eg
        if (info.recordCode != null) console.log("Record Code: " + info.recordCode);
        if (info.comment != null) console.log("Comment: " + info.comment);
        console.groupEnd();

        //Finished logging the action to the console.
        return true;
    } catch(e) {
        //Log the error to the console...eg
        console.error(e.message);
        return false;
    }
}

/**
 *
 * @param {*} newThis - The new scope to use.
 * @param {function} fnToCall - The function to call with the specified 'this' scope.
 * @param {*|Array} [args = []]- Single argument or array of arguments.
 */
export function resetThisScope(newThis, fnToCall, args = []) {
    return fnToCall.apply(newThis, Array.isArray(args) ? args : (args ? [args] : []));
}

/**
 * Shows an input box and then creates the entity based on the name entered
 * @param {string} title - The title of the input box
 * @param {string} message - The message in the input box
 * @param {string} defaultText - The default text in the input box
 * @param {string} blankTextMessage - The message displayed if the input is blank when ok is pressed
 * @param {string} OKText - The button text of the OK button
 * @param {string} CancelText - The button text of the Cancel button
 * @param {function} callbackOK - the callback function for the OK button
 * @param {function} callbackCancel - the callback function for the Cancel button
 * @param {number} [type = app.dv.types.MessageTypes.Information] - The message box type
 * @param {boolean} [showCancel = true] - Flag to show/hide the cancel button
 */
export function showInputOkCancel(title, message, defaultText, blankTextMessage, OKText, CancelText, callbackOK, callbackCancel, type = app.dv.types.MessageTypes.Information, showCancel = true) {
    //Set defaults
    if (title == null || title === "") title = "Input Value";
    if (message == null) message = "";
    if (blankTextMessage == null) blankTextMessage = "";
    if (defaultText == null) defaultText = "";
    if (OKText == null || OKText === "") OKText = "OK";
    if (CancelText == null || CancelText === "") CancelText = "Cancel";

    //display message to receive user input on the new record type...EG
    let inputPanel = new zebra.ui.dvMsgPanel(message);

    //Set the Header color based on the message type...eg
    //Set to an information panel 0...eg
    let win = app.dv.createUserInputPrompt(type, inputPanel, title, defaultText),
        buttons = [];

    //Add the OK button to the input...eg
    let btnOK = inputPanel.addButton(OKText, () => {
        //OK button was clicked,  Get the input value fr the new view to be created..MD
        let inputValue = win.getInputValue();

        //Check to make sure input value is not null, if it is show a toast message and do no hide the input screen...md
        if (blankTextMessage !== "" && (inputValue === "")) {
            app.browser.notifier.showToast(blankTextMessage, 3000, 0);
            return;
        }

        //callback with the input value...eg
        if (callbackOK != null) {
            Promise.resolve(callbackOK(inputValue)).then(function () {
                win.closeWindow();
            });
        }
        else {
            win.closeWindow();
        }
    });
    buttons.push(btnOK);

    if (showCancel) {
        //Add the Cancel button to the messagebox...eg
        let btnCancel = inputPanel.addButton(CancelText, () => {
            //callback when message is cancelled...eg
            if (callbackCancel != null) {
                Promise.resolve(callbackCancel(null)).then(function() {
                    win.closeWindow();
                });
            }
            else {
                win.closeWindow();
            }
        });
        buttons.push(btnCancel);
    }

    //Format the current button for the messagebox....eg
    win.formatButtons(buttons);

    app.dv.userWorkspace.windowManager.addWindow(win).then( (winObj) => {
        win.activatePanel();
        return null;
    });
}

/**
 * Search for the specified property UID in the specified expression string.
 * @param {string} propertyUID - The UID of the property to search for.
 * @param {string|array} expression - The expression to search through.
 * @return {boolean} - TRUE, property UID was found in the expression; FALSE, property UID was not found in the expression.
 */
export function propertyInExpression (propertyUID, expression) {
    if (propertyUID == null || expression == null) {
        return false;
    }

    return expression.indexOf(`[${propertyUID.toString()}]`) >= 0 || expression.indexOf(`<${propertyUID.toString()}>`) >= 0
}

/**
 * Method to call for creating and returning a new EntityLinker object.
 * @param {string|EntityTypes} type - The type of entity linker to create or a string value of the type.
 * @param {object|string} uidOrObject - The entity object to create the linker for with a UID property, or the UID string.
 * @param {object} [modifiers = null] - (OPTIONAL) If specified, create a "modifiers" object on the entity linker that is this specified object.
 * @returns {EntityLinker}
 */
export function createEntityLinker(type, uidOrObject, modifiers = null) {
    return new EntityLinker(type, uidOrObject, modifiers);
}

/**
 * Return the MVC of the specified control, or NULL if none was found.
 * @param {object} control - A control that we are trying to find the MVC for.
 * @param {Boolean} findTop - True, returns the top-most mvc. False, returns the nearest mvc
 * @return {MVC|null} - The MVC object for the specified control, or NULL if none was found.
 */
export function findMvcFromControl(control, findTop = false) {
    // get the parent of the specified control.
    let p = control.parent;

    // while "p" is not null, do the operation to thread back up the layers...
    while (p != null) {
        // if this layer has a "mvc" property, then we found the nearest level mvc.
        if (p.mvc != null && !(findTop && p !== p.mvc.rootPanel)) {
            return p.mvc;
        }
        p = p.parent;
    }
    return null;
}

/**
 * Function to attempt to scroll any containing scroll panels to make the specified control visible in the window.
 * @param {Object} control - The control to scroll into view.
 * @param {Boolean} [scrollVertical = true] - Flag that determines if the function should scroll vertically.
 * @param {Boolean} [scrollHorizontal = true] - Flag that determines if the function should scroll horizontally.
 */
export function scrollIntoView (control, scrollVertical = true, scrollHorizontal = true) {
    let p = control.parent,
        posY = control.y,
        posX = control.x;

    while (p != null) {
        if (zebra.instanceOf(p, zebra.ui.ScrollPan) && (p.parent == null || p.parent.textField == null)) {
            // Handle vertical offsets
            if (scrollVertical) {
                let vScrollAmount = 0,
                    hBarHeight = (!p.autoHide && p.hBar && p.hBar.isVisible && p.hBar.height) || 0;
                if (posY + control.height > p.height - hBarHeight) {
                    // Overflow on the bottom, scroll down by the overflowed amount to show it
                    vScrollAmount = posY + control.height - p.height + hBarHeight;
                }
                else if (posY < 0) {
                    // Overflow on the top, scroll up enough to make as much of the top visible without overflowing the right.
                    vScrollAmount = Math.max(posY, posY + control.height - p.height + hBarHeight);
                }
                if (p.vBar && vScrollAmount !== 0) {
                    let newOffset = Math.min(p.vBar.position.offset + vScrollAmount, p.vBar.getMaxOffset());
                    vScrollAmount = newOffset - p.vBar.position.offset;
                    p.vBar.position.setOffset(newOffset, true);
                    posY -= vScrollAmount;
                }
            }

            // Handle horizontal offsets
            if (scrollHorizontal) {
                let hScrollAmount = 0,
                    vBarWidth = (!p.autoHide && p.vBar && p.vBar.isVisible && p.vBar.width) || 0;
                if (posX + control.width > p.width - vBarWidth) {
                    // Overflow on the right, scroll right by the overflowed amount to show it
                    hScrollAmount = posX + control.width - p.width + vBarWidth;
                }
                else if (posX < 0) {
                    // Overflow on the left, scroll left enough to make as much of the top visible without overflowing the bottom.
                    hScrollAmount = Math.max(posX, posX + control.width - p.width + vBarWidth);
                }
                if (p.hBar && hScrollAmount !== 0) {
                    let newOffset = Math.min(p.hBar.position.offset + hScrollAmount, p.hBar.getMaxOffset());
                    hScrollAmount = newOffset - p.hBar.position.offset;
                    p.hBar.position.setOffset(newOffset, true);
                    posX -= hScrollAmount;
                }
            }
        }
        posY += p.y;
        posX += p.x;
        p = p.parent;
    }
}

/**
 * Find the parent of the specified control that is a dvLayoutItem control (useful for finding the control with a layout definition).
 * @param {zebra.ui.Layoutable} control - The zebra.ui control to start to find the parent that is a dvLayoutItem control.
 * @returns {zebra.ui.dvLayoutItem|null} - Returns the dvLayoutItem parent, or NULL if none was found.
 */
export function findLayoutItemFromControl (control) {
    // get the parent of the specified control.
    let p = control.parent;

    // while "p" is not null, do the operation to thread back up the layers...
    while (p != null) {
        // if this is a dvLayoutItem, we have the control.
        if (zebra.instanceOf(p, zebra.ui.dvLayoutItem)) {
            return p;
        }
        p = p.parent;
    }
    return null;
}

/**
 * Check that the passed zebra.ui control is in a valid context for layout.
 * @param {zebra.ui.Layoutable|zebra.ui.Panel} control
 * @returns {boolean} - TRUE, control is in valid UI context; FALSE, control is not in valid UI context
 */
export function ensureValidHierarchy (control) {
    let p = control.parent,
        canvas = control.getCanvas();

    if (canvas == null) {
        return false;
    }

    while (p != null) {
        if (zebra.instanceOf(p, zebra.ui.dvMainPanel) || p.$hash$ === canvas.$hash$) {
            return true;
        }
        p = p.parent;
    }
    return false;
}

/**
 * Gets the names of all embedded form controls in the form or mvc
 * NOTE: This does not check into bound embedded forms
 * @param {!Object} searchParent - The form or mvc we are evaluating
 * @returns {Array} An array of embedded for control names (strings)
 */
export function getEmbeddedFormControlIDs (searchParent) {

    let embeddedFormControlIDs = [];

    /**
     * Recursive function that interrogates through the children of the formLayout for embedded forms
     * @param {Array} children - The current array of children in the form layout
     */
    let recursiveSearch = (children) => {
        if (children && children.length > 0) {
            children.forEach((child) => {
                if (child.ViewID) {
                    embeddedFormControlIDs.push(child.ID);
                }
                recursiveSearch(child.Children);
            });
        }
    };

    if (searchParent.ViewID) {
        // We have a view instance, search through the formLayout definitions for any embedded forms
        recursiveSearch(searchParent.Definition.FormLayout);
    }
    else if (searchParent.formController) {
        // We have a non-hidden mvc, get the names of any embedded forms in the form controller
        searchParent.formController.formViews.Values.forEach((UIGroup) => {
            UIGroup.uiControl.forEach((control) => {
                if (control.subView) {
                    embeddedFormControlIDs.push(control.definition.ID);
                }
            });
        });
    }
    else if (searchParent.drivingView) {
        // We have a hidden mvc, search through the driving view's FormLayout
        recursiveSearch(searchParent.drivingView.Definition.FormLayout);
    }

    return embeddedFormControlIDs;
}

/**
 * Get the names of all unique control IDs in the form or MVC.
 * NOTE: this does not check into embedded forms.
 * @param {MVC|View} searchParent - The view or MVC to search for controls.
 * @returns {Array} - Array of unique IDs
 */
export function getControlIDs(searchParent) {
    let formControlIDs = [];

    /**
     * Recursive search for any control with an ID in the specified definition or any child.
     * @param {LayoutItem} layoutDef - The layout definition to search.
     * @param {Array} foundArray - Array of found items used during recursion.
     * @returns {Array}
     */
    let recursiveSearch = (layoutDef, foundArray) => {
        if (layoutDef != null) {
            // if this control has a unique ID, then add it.
            if (layoutDef.ID != null && layoutDef.ID !== '' && foundArray.indexOf(layoutDef.ID) === -1) {
                foundArray.push(layoutDef.ID);
            }

            // search through the control's kids if it is not an embedded form.
            if (layoutDef.ViewID == null && Array.isArray(layoutDef.Children) && layoutDef.Children.length > 0) {
                layoutDef.Children.forEach((child) => {
                    recursiveSearch(child, foundArray);
                });
            }
        }

        // return the list of IDs.
        return foundArray;
    };

    /**
     * Recursive search for any control with an ID in the specified definition or any child.
     * @param {zebra.ui.Layoutable|zebra.ui.Panel|BasePanel} control - The control to search.
     * @param {Array} foundArray
     * @returns {Array}
     */
    let recursiveControlSearch = (control, foundArray) => {
        if (control != null) {
            // if this control has a unique ID, then add it.
            if (control.id != null && control.id !== '' && foundArray.indexOf(control.id) === -1) {
                foundArray.push(control.id);
            }

            // search through the control's kids.
            if (Array.isArray(control.kids) && control.kids.length > 0) {
                control.kids.forEach((child) => {
                    recursiveControlSearch(child, foundArray);
                });
            }
        }

        // return the list of IDs.
        return foundArray;
    };

    if (searchParent.ViewID != null) {
        return recursiveSearch(searchParent.Definition.FormLayout, formControlIDs);
    }
    else if (searchParent.drivingView && searchParent.formController) {
        // this is an MVC with a form controller.
        return recursiveControlSearch(searchParent.formController.uiControl, formControlIDs);
    }

}

//#region Expression helpers

function replacePropIDinExpr(expr, p){
    //Escape valid regex syntax characters
    let propName = escapeRegExp(p.Definition.Name);
    //For each available property, use a string replace to set them back to IDs...EK
    expr = expr.replace(new RegExp("\\[" + propName + "\\]", "g"), "[" + String(p.UID) + "]");
    expr = expr.replace(new RegExp("\\<" + propName + "\\>", "g"), "<" + String(p.UID) + ">");
    return expr;
}

//This function takes an expression and changes the field (stored as [1234]) to the property name of the id so it is easier to read...EK
let allColumnUIDs = /(\[{1}\"{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\"{0,1}\]{1})/g;
let allGridColumnUIDs = /(\<{1}\'{0,1}([0-9a-fA-F]){8}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){4}-([0-9a-fA-F]){12}\'{0,1}\>{1})/g;
let allBracketsAndDoubleQuotes = /[\[\]\"]/g;
let allAngleBracketsAndDoubleQuotes = /[\<\>\"]/g;
export function showExpressionFieldsAsNames(expression) {
    if (expression == null || expression === "") return "";

    //Use a regular expression to identify the fields stored as IDs...EK
    let matchSet = expression.match(allColumnUIDs);
    //Looks for < > wrapped properties that will be eventually replaced with Grid Values...BC
    let objectMatchSet = expression.match(allGridColumnUIDs);

    for (let i in matchSet) {
        if (matchSet.hasOwnProperty(i)) {
            let currMatch = matchSet[i];
            let propertyID = currMatch.replace(allBracketsAndDoubleQuotes, '');

            let propFromCache = app.dv.cache.properties.allProperties.getVal({"Type": "Property", "UID": propertyID});
            if (propFromCache != null) {
                expression = expression.replace(new RegExp(currMatch.escapeChars(), "g"), '[' + propFromCache.Definition.Name + ']');
            }
        }
    }

    //Replace the property ID inside <> with the name for easier readability...BC
    for (let i in objectMatchSet) {
        if (objectMatchSet.hasOwnProperty(i)) {
            let currObjMatch = objectMatchSet[i];
            let propertyID = currObjMatch.replace(allAngleBracketsAndDoubleQuotes, '');

            let propFromCache = app.dv.cache.properties.allProperties.getVal({"Type": "Property", "UID": propertyID});
            if (propFromCache != null) {
                expression = expression.replace(new RegExp(currObjMatch.escapeChars(), "g"), '<' + propFromCache.Definition.Name + '>');
            }
        }
    }

    return expression;
}

//This function takes an expression and replaces it's names with the ID of the property it represents (it must be in this form to execute) ... EK
// modified to handle arrays and objects (labels, etc) MD
export function storeExpressionFieldsAsIDs(expression, possibleProperties) {
    if (expression == null || expression === "" || possibleProperties == null || possibleProperties.length === 0) return expression;
    //if the object is an array,...MF
    if (Array.isArray(possibleProperties)){
        for (let prop of possibleProperties) {
            expression = replacePropIDinExpr(expression, prop);
        }
    } else { //loop through the object to access the properties...MD
        for (let key in possibleProperties) {
            if (possibleProperties.hasOwnProperty(key)){
                let prop =  possibleProperties[key];
                if (prop.Definition != null && prop.Definition.Name != null) {
                    expression = replacePropIDinExpr(expression, prop);
                }
            }
        }
    }

    return expression;
}

/**
 * Attempts to evaluate the expression and return the results.
 * NOTE: This will throw an error if the expression is invalid.
 * @param {String} expression - the expression string with IDs instead of column names
 * @param {Object} mvc - the optional mvc to run the expression on.
 * @returns {*} - The result from evaluating the expression.
 */
export function evaluateExpression(expression, mvc) {
    if (expression !== "" && (expression[0] === "?" || expression[0] === "=")) {
        //Need to strip the "?" or "=" before evaluating the expression.
        expression = expression.substring(1);
    }

    let viewID = getOrDefault(mvc, 'drivingView.ViewID', 0),
        expr = null;
    try {
        expr = app.dv.helpers.expression.create(null, viewID, expression, null, mvc);
    }
    catch (e) {
        if (app.designer) {
            app.browser.notifier.showToast(`Error creating adhoc expression:<br/>${expression}<br/>Error: ${e.message}`);
        }
    }
    if (expr) {
        try {
            return expr.evaluate(viewID, mvc != null ? mvc.selectedRecord : null);
        }
        catch (e) {
            if (app.designer) {
                app.browser.notifier.showToast(`Error evaluating adhoc expression:<br/>${expression}<br/>Error: ${e.message}`);
            }
        }
    }
    return null;
}
//#endregion

//#region Entity Name Conversion to a displayable format
/**
 * Convert the entity name to a more user friendly display name. Used initially in the tag editor and package editor.
 * @param entityName The name of the entity to get a name conversion for
 * @returns {*} the converted name
 */
export function getEntityTypeForDisplay(entityType) {
    //ensure we have a name to convert
    if (entityType == null ||entityType == '') {return entityType;}

    switch (entityType) {
        case app.dv.types.EntityTypes.Action:
            return "Custom Action";
            break;
        case app.dv.types.EntityTypes.RecordType:
            return "Table";
            break;
        case app.dv.types.EntityTypes.Category:
            return "Category";
            break;
        case app.dv.types.EntityTypes.Property:
            return "Column";
            break;
        case app.dv.types.EntityTypes.ValueStatus:
            return "Status";
            break;
        case app.dv.types.EntityTypes.View:
            return "Form";
            break;
        case app.dv.types.EntityTypes.SecurityGroup:
            return "Security Group";
            break;
        case app.dv.types.EntityTypes.SecurityGroupLookups:
            return "Security Group Lookups";
            break;
        case app.dv.types.EntityTypes.Security:
            return "Security";
            break;
        case app.dv.types.EntityTypes.Style:
            return "Style";
            break;
        case app.dv.types.EntityTypes.Tag:
            return "Tag"
            break;
        case app.dv.types.EntityTypes.Expression:
            return "Custom Function";
            break;
        case app.dv.types.EntityTypes.Package:
            return "Package"
            break;
        case 'PackageLink':
            return 'Package Link';
            break;
        case 'TagLink':
            return 'Tag Link';
            break;
        case 'User':
            return 'User';
            break;
        default:
            return entityType;
    }
}
//#endregion

export function isDate (value) {
    if ( Object.prototype.toString.call(value) === "[object Date]" ) {
        // it is a date
        return Number.isNumeric(value.getTime());
    }
    else {
        return false;
    }
}

/**
 * Load any styles in the definition of the specified target control as local cloned copies of the style entity on the control.
 * NOTE: If any local cloned copies are currently on the control, they will be disposed and cleared before applying fresh ones.
 * @param {zebra.ui.Layoutable} target
 */
export function loadStyleObjects (target) {
    if (Array.isArray(target.styles)) {
        // we have an array of loaded style objects; dispose of them.
        target.styles.forEach((loadedStyle) => {
            loadedStyle.dispose();
        });
        target.styles.length = 0;
    }

    if (target.definition != null && Array.isArray(target.definition.Styles)) {
        // we have a definition of style entity linkers; reload the styles on the control with these.
        target.definition.Styles.forEach((styleLinker) => {
            if (styleLinker != null && styleLinker.UID != null) {
                // get the style, then immediately clone it for use in our control.
                // NOTE: we are using the full reference from "app" so we prevent circular references for imports.
                let clonedStyle = app.dv.mvc.formController.cloneStyleInstance(app.dv.cache.styles.allStyles.getVal(styleLinker));
                if (clonedStyle != null) {
                    if (styleLinker.Modifiers != null
                        && styleLinker.Modifiers.IsPinned != null) {
                        // if the linker is pinned, set the client properties flag on the cloned style to show this.
                        clonedStyle.clientProperties.isPinned = styleLinker.Modifiers.IsPinned;
                    }
                    // ensure there is a styles array...
                    if (!Array.isArray(target.styles)) {
                        target.styles = [];
                    }
                    target.styles.push(clonedStyle);
                }
            }
        });
    }
}
